export type FuelChangeoverPresetField = {
  label: string
  fieldType: string
  readOnly: boolean
  required: boolean
  active?: boolean
  unit?: string
  options?: string[] | null
}

export type FuelChangeoverPresetSection = {
  name: string
  fields: FuelChangeoverPresetField[]
}

/**
 * Helper to identify if a given report name represents a Fuel Changeover report.
 */
export const isFuelChangeoverReportName = (name?: string | null): boolean =>
  /\b(?:fuel\s*change\s*over|fuel\s*changeover|changeover)\b/i.test(String(name ?? ''))

export interface TankWiseFuelRow {
  id?: string | number
  tankName: string
  q88Grade: string
  fuelType: string
  soundingRob: string | number
  consumedTransferred: string | number
  currentRob: string | number
  sulphurPercentage: string
  allocationStatus: string
  remarks?: string
}

export const COMMON_Q88_FUEL_GRADES = [
  { grade: 'VLSFO 0.5% S (RMG 380)', fuelType: 'VLSFO', sulphur: '0.48%' },
  { grade: 'LSMGO 0.1% S (DMA)', fuelType: 'LSMGO', sulphur: '0.09%' },
  { grade: 'HSFO 3.5% S (RMK 700)', fuelType: 'HSFO', sulphur: '2.80%' },
  { grade: 'ULSFO 0.1% S (RMD 80)', fuelType: 'ULSFO', sulphur: '0.08%' },
  { grade: 'MDO (DMB)', fuelType: 'MDO', sulphur: '0.85%' },
  { grade: 'Bio-Fuel B30', fuelType: 'BioFuel', sulphur: '0.05%' },
  { grade: 'LNG', fuelType: 'LNG', sulphur: '0.00%' },
  { grade: 'Methanol', fuelType: 'Methanol', sulphur: '0.00%' },
  { grade: 'Others', fuelType: 'Other', sulphur: '--' },
] as const

export const DEFAULT_FALLBACK_TANKS = [
  { name: 'HFO 1 (P) Storage Tank', defaultGrade: 'VLSFO 0.5% S (RMG 380)', fuelType: 'VLSFO', status: 'Storage Tank' },
  { name: 'HFO 1 (S) Storage Tank', defaultGrade: 'VLSFO 0.5% S (RMG 380)', fuelType: 'VLSFO', status: 'Storage Tank' },
  { name: 'HFO Service Tank', defaultGrade: 'VLSFO 0.5% S (RMG 380)', fuelType: 'VLSFO', status: 'In Service (Pre-changeover)' },
  { name: 'HFO Settling Tank', defaultGrade: 'VLSFO 0.5% S (RMG 380)', fuelType: 'VLSFO', status: 'Settling / Standby' },
  { name: 'LSMGO Storage (P) ECA Storage Tank', defaultGrade: 'LSMGO 0.1% S (DMA)', fuelType: 'LSMGO', status: 'ECA Storage Tank' },
  { name: 'LSMGO Storage (S) ECA Storage Tank', defaultGrade: 'LSMGO 0.1% S (DMA)', fuelType: 'LSMGO', status: 'ECA Storage Tank' },
  { name: 'LSMGO Service Tank', defaultGrade: 'LSMGO 0.1% S (DMA)', fuelType: 'LSMGO', status: 'Changeover Active Service Tank' },
  { name: 'LSMGO Settling Tank', defaultGrade: 'LSMGO 0.1% S (DMA)', fuelType: 'LSMGO', status: 'Settling / Standby' },
] as const

export const TANK_ALLOCATION_STATUS_OPTIONS = [
  'Storage Tank',
  'In Service (Pre-changeover)',
  'Settling / Standby',
  'ECA Storage Tank',
  'Changeover Active Service Tank',
  'Reserve / Unallocated',
] as const

const cleanNum = (v: any): number => {
  if (v === null || v === undefined || v === '') return 0
  const n = Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

/**
 * Synthesizes tank-wise fuel rows strictly from submitted position report consumption rows for the voyage.
 * Returns only the tanks/fuels actually submitted for the active voyage.
 */
export const synthesizeTankWiseFuelRows = (
  submittedRows: any[] = [],
  _tanksSource?: readonly { name: string; defaultGrade: string; fuelType: string; status: string }[] | any[]
): TankWiseFuelRow[] => {
  if (!Array.isArray(submittedRows) || submittedRows.length === 0) {
    return []
  }

  const validRows = submittedRows.filter(
    (r: any) => Boolean(r?.tankName || r?.tank_name || r?.fieldType || r?.fuelType)
  )

  if (validRows.length === 0) {
    return []
  }

  return validRows.map((r: any, idx: number) => {
    const sound = cleanNum(r.initialRob ?? r.initial_rob ?? '0')
    const cur = cleanNum(r.lastRob ?? r.rob ?? r.last_rob ?? '0')
    const machConsumed = (Array.isArray(r.machineries) ? r.machineries : []).reduce(
      (sum: number, m: any) => sum + cleanNum(m?.consumed),
      0
    )
    const directConsumed = cleanNum(r.consumedTransferred ?? r.consumed ?? '')
    const consumed = machConsumed > 0 ? machConsumed : directConsumed > 0 ? directConsumed : Math.max(sound - cur, 0)
    
    const fuelTypeVal = String(r.fieldType || r.fuelType || '').trim()
    const isVlsfo = fuelTypeVal.toUpperCase().includes('VLSFO') || fuelTypeVal.toUpperCase().includes('HFO') || fuelTypeVal.toUpperCase().includes('HSFO')
    const isLsmgo = fuelTypeVal.toUpperCase().includes('LSMGO') || fuelTypeVal.toUpperCase().includes('MGO') || fuelTypeVal.toUpperCase().includes('ULSFO')
    
    const defaultTankName = isVlsfo
      ? 'HFO 1 (P) Storage Tank'
      : isLsmgo
      ? 'LSMGO Service Tank'
      : fuelTypeVal ? `${fuelTypeVal} Tank` : `Tank ${idx + 1}`

    const tankNameVal = String(r.tankName || r.tank_name || defaultTankName)
    
    const gradeMatch = COMMON_Q88_FUEL_GRADES.find(
      (g) =>
        g.grade === (r.q88Grade || r.q88_grade) ||
        (fuelTypeVal && g.fuelType.toUpperCase() === fuelTypeVal.toUpperCase())
    )

    const resolvedGrade = String(
      r.q88Grade || r.q88_grade || gradeMatch?.grade || (fuelTypeVal.toUpperCase().includes('LSMGO') ? 'LSMGO 0.1% S (DMA)' : 'VLSFO 0.5% S (RMG 380)')
    )
    const resolvedFuelType = String(
      r.fuelType || r.fieldType || gradeMatch?.fuelType || (resolvedGrade.includes('LSMGO') ? 'LSMGO' : 'VLSFO')
    )
    const resolvedSulphur = String(
      r.sulphurPercentage || r.sulphur_percentage || gradeMatch?.sulphur || (resolvedFuelType.toUpperCase().includes('LSMGO') ? '0.09%' : '0.48%')
    )
    const resolvedStatus = String(
      r.allocationStatus || r.allocation_status || (tankNameVal.toLowerCase().includes('service') ? 'In Service (Pre-changeover)' : 'Storage Tank')
    )

    return {
      id: idx + 1,
      tankName: tankNameVal,
      q88Grade: resolvedGrade,
      fuelType: resolvedFuelType,
      soundingRob: sound,
      consumedTransferred: consumed,
      currentRob: cur || Math.max(sound - consumed, 0),
      sulphurPercentage: resolvedSulphur,
      allocationStatus: resolvedStatus,
    }
  })
}

export const FUEL_CHANGEOVER_PRESET_SECTIONS: FuelChangeoverPresetSection[] = [
  {
    name: 'General Information',
    fields: [
      {label: 'Status', fieldType: 'select', readOnly: false, required: true, active: true, options: ['Ballast', 'Laden', 'In Port', 'At Anchor']},
      {label: 'Vessel Condition', fieldType: 'select', readOnly: false, required: true, active: true, options: ['At Sea', 'At Anchor', 'At Berth', 'Drifting']},
      {label: 'Departure Port', fieldType: 'text', readOnly: false, required: false, active: true},
      {label: 'Arrival Port', fieldType: 'text', readOnly: false, required: false, active: true},
      {label: 'Vessel Name', fieldType: 'text', readOnly: true, required: true, active: true},
      {label: 'Voyage Number', fieldType: 'text', readOnly: true, required: true, active: true},
      {label: 'Report Type', fieldType: 'text', readOnly: true, required: true, active: true},
      {label: 'Report Date Time', fieldType: 'datetime', readOnly: false, required: true, active: true},
    ],
  },
  {
    name: 'Vessel Position',
    fields: [
      {label: 'Latitude Deg', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'Deg'},
      {label: 'Latitude Min', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'min'},
      {label: 'Latitude Sec', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'sec'},
      {label: 'Latitude Direction', fieldType: 'select', readOnly: false, required: false, active: true, options: ['N', 'S']},
      {label: 'Longitude Deg', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'Deg'},
      {label: 'Longitude Min', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'min'},
      {label: 'Longitude Sec', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'sec'},
      {label: 'Longitude Direction', fieldType: 'select', readOnly: false, required: false, active: true, options: ['E', 'W']},
    ],
  },
  {
    name: 'Changeover Details',
    fields: [
      {
        label: 'Changeover Event Type',
        fieldType: 'select',
        readOnly: false,
        required: true,
        active: true,
        options: [
          'ECA Entry (Start → Commencement → Completion → Entry)',
          'ECA Exit (Exit → Start → Commencement → Completion)',
        ],
      },
      {
        label: 'ECA Zone',
        fieldType: 'select',
        readOnly: false,
        required: true,
        active: true,
        options: [
          'Baltic Sea',
          'North Sea',
          'North American ECA',
          'US Caribbean ECA',
          'Mediterranean ECA',
          'Other',
        ],
      },
      {
        label: 'Changeover Direction',
        fieldType: 'select',
        readOnly: false,
        required: true,
        active: true,
        options: ['Entering ECA (High to Low Sulphur)', 'Exiting ECA (Low to High Sulphur)'],
      },
      {
        label: 'Event Sequence Step',
        fieldType: 'select',
        readOnly: false,
        required: false,
        active: true,
        options: ['Commencement', 'Completion', 'ECA Entry', 'ECA Exit'],
      },
      {label: 'Event Date Time', fieldType: 'datetime', readOnly: false, required: true, active: true},
      {label: 'Changeover Commence Date Time', fieldType: 'datetime', readOnly: false, required: true, active: true},
      {label: 'Changeover Complete Date Time', fieldType: 'datetime', readOnly: false, required: true, active: true},
      {label: 'ECA Entry Date Time', fieldType: 'datetime', readOnly: false, required: false, active: true},
      {label: 'ECA Exit Date Time', fieldType: 'datetime', readOnly: false, required: false, active: true},
      {label: 'Duration for Changeover', fieldType: 'number', readOnly: false, required: false, active: true, unit: 'Hrs'},
      {
        label: 'Changeover From Fuel',
        fieldType: 'select',
        readOnly: false,
        required: true,
        active: true,
        options: ['HFO', 'VLSFO', 'LSFO', 'MGO', 'MDO', 'ULSFO', 'LNG', 'BioFuel', 'LPG'],
      },
      {
        label: 'Changeover To Fuel',
        fieldType: 'select',
        readOnly: false,
        required: true,
        active: true,
        options: ['HFO', 'VLSFO', 'LSFO', 'MGO', 'MDO', 'ULSFO', 'LNG', 'BioFuel', 'LPG'],
      },
      {label: 'Fuel Sulphur % Before Changeover', fieldType: 'number', readOnly: false, required: false, active: true, unit: '%'},
      {label: 'Fuel Sulphur % After Changeover', fieldType: 'number', readOnly: false, required: false, active: true, unit: '%'},
      {label: 'Changeover Remarks', fieldType: 'textarea', readOnly: false, required: false, active: true},
    ],
  },
  {
    name: 'Consumption & ROB',
    fields: [
      {label: 'Fuel Type', fieldType: 'select', readOnly: false, required: true, active: true},
      {label: 'Initial ROB', fieldType: 'number', readOnly: false, required: true, active: true, unit: 'MT'},
      {label: 'ROB', fieldType: 'number', readOnly: false, required: true, active: true, unit: 'MT'},
      {label: 'Machinery', fieldType: 'text', readOnly: false, required: true, active: true},
      {label: 'Operation Type', fieldType: 'text', readOnly: false, required: false, active: true},
      {label: 'Consumed', fieldType: 'number', readOnly: false, required: true, active: true, unit: 'MT'},
    ],
  },
]

/**
 * Ensures all standard preset fields for Fuel Changeover are present in a given section,
 * complementing any existing fields returned from sparse backend templates.
 */
export const ensureFuelChangeoverPresetFields = (
  sectionTitle: string,
  existingFields: any[] = []
): any[] => {
  const normKey = (s: any) => {
    let raw = String(s ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    raw = raw.replace(/\bstart\b/g, 'commencement');
    raw = raw.replace(/\bcommence\b/g, 'commencement');
    raw = raw.replace(/\btime\b/g, 'date time');
    raw = raw.replace(/\bdatetime\b/g, 'date time');
    raw = raw.replace(/\b(at|of|the|a)\b/g, '').replace(/\s+/g, ' ').trim();
    return raw;
  };

  const norm = (s: any) =>
    String(s ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const isChangeoverSec =
    norm(sectionTitle).includes('change over') ||
    norm(sectionTitle).includes('changeover');

  if (!isChangeoverSec) return existingFields;

  const defaultPositionFields: FuelChangeoverPresetField[] = [
    { label: 'Position at Commencement of Changeover', fieldType: 'position', readOnly: false, required: true, active: true },
    { label: 'Position at Completion of Changeover', fieldType: 'position', readOnly: false, required: false, active: true },
    { label: 'Position at ECA Entry', fieldType: 'position', readOnly: false, required: false, active: true },
    { label: 'Position at ECA Exit', fieldType: 'position', readOnly: false, required: false, active: true },
  ];

  const changeoverSection = FUEL_CHANGEOVER_PRESET_SECTIONS.find(
    (s) => norm(s.name).includes('changeover') || norm(s.name).includes('change over')
  );

  const presetFields: FuelChangeoverPresetField[] = [
    ...defaultPositionFields,
    ...(changeoverSection?.fields ?? []),
  ];

  const presetMap = new Map<string, FuelChangeoverPresetField>();
  presetFields.forEach((pf) => presetMap.set(normKey(pf.label), pf));

  const deduplicatedExisting: any[] = [];
  const seenExisting = new Set<string>();

  for (const ef of existingFields) {
    const key = normKey(ef.label);
    if (!key || seenExisting.has(key)) continue;
    seenExisting.add(key);

    const pf = presetMap.get(key);
    const updated = { ...ef };

    if (pf) {
      if (pf.fieldType === 'position' || !updated.fieldType) {
        updated.fieldType = pf.fieldType;
      }
      if (
        pf.options &&
        pf.options.length > 0 &&
        (!Array.isArray(updated.optionsJson) || updated.optionsJson.length === 0)
      ) {
        updated.optionsJson = pf.options;
      }
      if (pf.unit && !updated.unit) {
        updated.unit = pf.unit;
      }
    }

    deduplicatedExisting.push(updated);
  }

  const merged = [...deduplicatedExisting];
  let nextSyntheticId = 99100;

  // If existingFields already has template fields from backend DB,
  // do not force-inject extra non-position preset fields into the template.
  const fieldsToAdd = existingFields.length > 0 ? defaultPositionFields : presetFields;

  for (const pf of fieldsToAdd) {
    const key = normKey(pf.label);
    if (!key || seenExisting.has(key)) continue;
    seenExisting.add(key);
    merged.push({
      id: nextSyntheticId++,
      label: pf.label,
      fieldType: pf.fieldType,
      readOnly: pf.readOnly,
      required: pf.required,
      isActive: pf.active ?? true,
      optionsJson: pf.options ?? undefined,
      unit: pf.unit,
      submenuId: existingFields[0]?.submenuId ?? 0,
    });
  }

  const orderMap = new Map<string, number>();
  presetFields.forEach((pf, idx) => orderMap.set(normKey(pf.label), (idx + 1) * 10));

  return merged.map((f, i) => {
    const key = normKey(f.label);
    const logicalOrder = orderMap.get(key) ?? 1000 + i;
    return {
      ...f,
      order: f.order ?? logicalOrder,
    };
  });
};
